'use client';

import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const partnerCategories = [
{
  title: 'Building Material Suppliers & Manufacturers',
  img: 'https://www.nexsys.group/assets/structural-node-B-eQctER.png',
  intro: 'Suppliers provide compatible raw materials, components, and finished products that integrate seamlessly with the NeXSYS system, supporting quality, scalability, and regional sourcing.',
  benefits: [
  'Access to pre-approved material specifications.',
  'Reliable integration with modular system components.',
  'Reduced sourcing risks and quality issues.',
  'Certification and training on NeXSYS material requirements.',
  'Strengthened market credibility as a certified supplier.']

},
{
  title: 'MEP Equipment & System Providers',
  img: 'https://www.nexsys.group/assets/mep-wall-inserts-CaTRF6Xw.png',
  intro: 'Manufacturers and suppliers of MEP equipment deliver systems designed or adapted for modular integration, ensuring efficiency in factory and on-site installation.',
  benefits: [
  'Pre-coordinated systems that minimize on-site adjustments.',
  'Improved installation efficiency and reduced errors.',
  'Access to NeXSYS design and installation guidelines.',
  'Training on modular-friendly equipment integration.',
  'Competitive advantage as a certified NeXSYS supplier.']

},
{
  title: 'Logistics Partners',
  img: 'https://www.nexsys.group/assets/modular-crane-construction-DzFZmVYi.png',
  intro: 'Logistics partners manage transportation, handling, and sequencing of NeXSYS components and modules, ensuring timely delivery from factory to site across local and international markets.',
  benefits: [
  'Efficient delivery from factory to site.',
  'Reduced risk of damage or delay to modules.',
  'Coordinated logistics for multi-location projects.',
  'Access to NeXSYS guidelines for handling and sequencing.',
  'Strengthened partnership with certified modular operations.']

}];


const benefitItems = [
'Access to growing market demand',
'Technical support and training',
'Quality certification programs',
'Long-term partnership agreements',
'Co-marketing opportunities',
'Priority access to new product lines'];


export default function SupplyChainPartnersPage() {
  const [formData, setFormData] = useState({ name: '', company: '', email: '', type: 'Select Partner Type' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async () => {
    if (!formData?.name || !formData?.company || !formData?.email || formData?.type === 'Select Partner Type') {
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: formData?.name,
          company: formData?.company,
          email: formData?.email,
          projectType: formData?.type,
          message: `Partner enquiry from Supply Chain Partners page. Partner Type: ${formData?.type}`,
        }),
      });
      if (res?.ok) {
        setStatus('success');
        setFormData({ name: '', company: '', email: '', type: 'Select Partner Type' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="pt-24 pb-12 bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-3xl">
              <span className="text-primary font-semibold uppercase tracking-wider text-sm">Supply Chain Partners</span>
              <h1 className="text-hero lg:text-hero-lg font-heading font-bold text-white mt-2 mb-6">
                Join Our Supply Chain Network
              </h1>
              <p className="text-xl text-white/80 leading-relaxed">
                Material suppliers, MEP equipment providers, and logistics partners integrate seamlessly into the <span className="font-bold text-white">NeXSYS</span> manufacturing and delivery ecosystem. Together, we ensure quality, efficiency, and reliability from factory to site.
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="section-padding bg-card">
          <div className="container mx-auto container-padding max-w-4xl">
            <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-6">
              Powering the <span className="text-nexsys-brand">NeXSYS</span> Ecosystem
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Our supply chain partners are essential to delivering the <span className="font-bold text-nexsys-brand">NeXSYS</span> promise of quality, speed, and reliability. From raw materials and MEP equipment to logistics and delivery, we work with certified suppliers who meet our rigorous standards for performance and integration. Together, we create a seamless flow from factory to site—enabling faster project delivery and consistent quality across every build.
            </p>
          </div>
        </section>

        {/* Partner Categories */}
        <section className="section-padding">
          <div className="container mx-auto container-padding">
            <div className="mb-12 lg:mb-16 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Partner Categories
              </h2>
              <p className="text-lg text-muted-foreground">We work with a diverse network of supply chain partners.</p>
            </div>
            <div className="grid lg:grid-cols-3 gap-8">
              {partnerCategories?.map((cat) =>
              <div key={cat?.title} className="bg-card rounded-xl overflow-hidden shadow-card border border-border">
                  <div className="p-6">
                    <h3 className="font-heading font-bold text-lg text-foreground mb-3">{cat?.title}</h3>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {cat?.intro?.split('NeXSYS')?.map((part, i, arr) =>
                    <React.Fragment key={i}>
                          {part}
                          {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                        </React.Fragment>
                    )}
                    </p>
                    <p className="font-semibold text-foreground text-sm mb-2">Benefits:</p>
                    <ul className="space-y-2">
                      {cat?.benefits?.map((b) =>
                    <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0 mt-0.5">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                          {b?.split('NeXSYS')?.map((part, i, arr) =>
                      <React.Fragment key={i}>
                              {part}
                              {i < arr?.length - 1 && <span className="font-bold text-nexsys-brand">NeXSYS</span>}
                            </React.Fragment>
                      )}
                        </li>
                    )}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Partnership Benefits */}
        <section className="section-padding bg-nexsys-surface">
          <div className="container mx-auto container-padding">
            <div className="mb-12 text-center">
              <h2 className="text-section lg:text-section-lg font-heading font-bold text-foreground mb-4">
                Partnership Benefits
              </h2>
              <p className="text-lg text-muted-foreground">
                Why companies choose to partner with <span className="font-bold text-nexsys-brand">NeXSYS</span>.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {benefitItems?.map((item) =>
              <div key={item} className="bg-card rounded-xl p-4 shadow-card border border-border flex items-center gap-3">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span className="text-sm font-medium text-foreground">{item}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Application Form */}
        <section className="section-padding bg-gradient-dark">
          <div className="container mx-auto container-padding">
            <div className="max-w-xl mx-auto">
              <h3 className="text-2xl font-heading font-bold text-white mb-8 text-center">
                Become a Supply Chain Partner
              </h3>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                {status === 'success' ? (
                  <div className="text-center py-6">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary mx-auto mb-4">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                    <p className="text-white text-lg font-semibold">Thank you! We&apos;ll be in touch soon.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Name"
                      value={formData?.name}
                      onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="text"
                      placeholder="Company"
                      value={formData?.company}
                      onChange={(e) => setFormData({ ...formData, company: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <input
                      type="email"
                      placeholder="Email"
                      value={formData?.email}
                      onChange={(e) => setFormData({ ...formData, email: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-primary" />
                    <select
                      value={formData?.type}
                      onChange={(e) => setFormData({ ...formData, type: e?.target?.value })}
                      className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white focus:outline-none focus:border-primary">
                      <option value="Select Partner Type">Select Partner Type</option>
                      <option value="Building Material Suppliers & Manufacturers">Building Material Suppliers &amp; Manufacturers</option>
                      <option value="MEP Equipment & System Providers">MEP Equipment &amp; System Providers</option>
                      <option value="Logistics Partners">Logistics Partners</option>
                    </select>
                    {status === 'error' && (
                      <p className="text-red-400 text-sm">Please fill in all fields and select a partner type.</p>
                    )}
                    <button
                      onClick={handleSubmit}
                      disabled={status === 'loading'}
                      className="w-full bg-banana text-banana-foreground font-semibold py-3 rounded-lg hover:bg-banana/90 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60">
                      {status === 'loading' ? 'Sending...' : 'Submit Application'}
                      {status !== 'loading' && (
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="m22 2-7 20-4-9-9-4Z" />
                          <path d="M22 2 11 13" />
                        </svg>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );

}